import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDocs,
  onSnapshot,
  query,
  where,
  writeBatch,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type {
  CompanyUpdate,
  FinancialItem,
  FounderUpdateExtraction,
  FounderUpdateMetricExtraction,
  InvestmentCase,
  InvestmentCompany,
  MonthlyUpdate,
  UpdateSentiment,
} from '../types';
import { db } from '../lib/firebase';
import {
  COLLECTION_COMPANY_UPDATES,
  COLLECTION_FINANCIAL_ITEMS,
  COLLECTION_INVESTMENT_CASES,
  COLLECTION_INVESTMENT_ITEMS,
  COLLECTION_MONTHLY_UPDATES,
  ownedItemDocId,
} from '../lib/firestorePaths';
import { migrateLegacyWorkspaceStateDocument } from '../lib/migrateLegacyWorkspaceDoc';
import { normalizeFinancialItem } from '../lib/assetRegistryNormalize';
import { normalizeDeckIntelligence } from '../utils/deckIntelligence';
import { normalizeFounderUpdateIntelligence } from '../utils/founderUpdateIntelligence';
import { clampRating, founderForStorage } from '../utils/investmentCase';
import { normalizeCountryCode } from '../utils/investmentCompanyUi';
import {
  clearLegacyLocalWorkspaceKeys,
  markWorkspaceOwner,
  resolveBootstrapWorkspace,
} from '../lib/localWorkspaceMigration';

export type FirestoreWorkspaceState = {
  financialItems: FinancialItem[];
  setFinancialItems: Dispatch<SetStateAction<FinancialItem[]>>;
  companies: InvestmentCompany[];
  setCompanies: Dispatch<SetStateAction<InvestmentCompany[]>>;
  investmentCases: InvestmentCase[];
  setInvestmentCases: Dispatch<SetStateAction<InvestmentCase[]>>;
  updates: CompanyUpdate[];
  setUpdates: Dispatch<SetStateAction<CompanyUpdate[]>>;
  monthlyUpdates: MonthlyUpdate[];
  setMonthlyUpdates: Dispatch<SetStateAction<MonthlyUpdate[]>>;
  loading: boolean;
  error: string | null;
};

const DEBOUNCE_MS = 450;

/**
 * Firestore rejects `undefined` anywhere in a document (including nested maps like
 * `deckIntelligence.sectionScores`); optional fields must be omitted, not set to undefined.
 */
function stripUndefinedFields(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, stripUndefinedDeep(v)])
  );
}

function stripUndefinedDeep(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => stripUndefinedDeep(item));
  }
  if (value !== null && typeof value === 'object') {
    return stripUndefinedFields(value as Record<string, unknown>);
  }
  return value;
}

/**
 * `merge: true` does not delete missing keys. Write `null` for fields that must be cleared
 * when status or country changes.
 */
function investmentCompanyToFirestorePayload(userId: string, c: InvestmentCompany): Record<string, unknown> {
  const base = stripUndefinedFields({
    userId,
    ...(c as unknown as Record<string, unknown>),
  });
  const st =
    c.investmentStatus === 'exit' || c.investmentStatus === 'dead' || c.investmentStatus === 'active'
      ? c.investmentStatus
      : 'active';
  if (st === 'active') {
    base.statusDate = null;
    base.exitTotalEuros = null;
  } else if (st === 'dead') {
    base.exitTotalEuros = null;
  }
  if (!normalizeCountryCode(c.countryCode)) {
    base.countryCode = null;
  }
  return base;
}

function extractedMetricsToFirestorePayload(
  metrics: FounderUpdateMetricExtraction
): Record<string, unknown> {
  return stripUndefinedFields(metrics as unknown as Record<string, unknown>);
}

function monthlyUpdateToFirestorePayload(userId: string, item: MonthlyUpdate): Record<string, unknown> {
  const { updateIntelligence, ...rest } = item;
  const base = stripUndefinedFields({
    userId,
    ...(rest as unknown as Record<string, unknown>),
  });
  if (updateIntelligence) {
    base.updateIntelligence = {
      ...updateIntelligence,
      extractedMetrics: extractedMetricsToFirestorePayload(updateIntelligence.extractedMetrics),
    };
  }
  return base;
}

function investmentCaseToFirestorePayload(userId: string, c: InvestmentCase): Record<string, unknown> {
  const { founders, ...rest } = c;
  const base = stripUndefinedFields({ userId, ...(rest as unknown as Record<string, unknown>) });
  const cleanedFounders = founders
    ?.map((f) => founderForStorage(f))
    .filter((f): f is NonNullable<typeof f> => f !== null);
  if (cleanedFounders?.length) {
    base.founders = cleanedFounders;
  } else if (founders !== undefined) {
    base.founders = null;
  }
  return base;
}

function fingerprint(
  fin: FinancialItem[],
  co: InvestmentCompany[],
  up: CompanyUpdate[],
  mu: MonthlyUpdate[],
  ic: InvestmentCase[]
): string {
  const sortById = <T extends { id: string }>(arr: T[]) => [...arr].sort((a, b) => a.id.localeCompare(b.id));
  return JSON.stringify({
    fin: sortById(fin),
    co: sortById(co),
    up: sortById(up),
    mu: sortById(mu),
    ic: sortById(ic),
  });
}

function docToFinancialItem(d: QueryDocumentSnapshot): FinancialItem {
  const x = d.data() as FinancialItem & { userId?: string };
  const { userId: _u, ...rest } = x;
  return normalizeFinancialItem(rest as FinancialItem);
}

function docToCompany(d: QueryDocumentSnapshot): InvestmentCompany {
  const x = d.data() as InvestmentCompany & {
    userId?: string;
    notes?: string;
    monthlyRevenue?: number;
    monthlySpend?: number;
  };
  const { userId: _u, notes: _notes, monthlyRevenue: _mr, monthlySpend: _ms, ...rest } = x;
  const co = rest as InvestmentCompany;
  const ticketSize =
    typeof co.ticketSize === 'number' && Number.isFinite(co.ticketSize) ? co.ticketSize : 0;
  const investmentDate = typeof co.investmentDate === 'string' ? co.investmentDate : '';
  const rawStatus = co.investmentStatus;
  const investmentStatus =
    rawStatus === 'exit' || rawStatus === 'dead' || rawStatus === 'active' ? rawStatus : undefined;
  const countryCode = normalizeCountryCode(co.countryCode);
  const statusDate =
    typeof co.statusDate === 'string' && co.statusDate.trim() ? co.statusDate : undefined;
  let exitTotalEuros =
    typeof co.exitTotalEuros === 'number' && Number.isFinite(co.exitTotalEuros)
      ? co.exitTotalEuros
      : undefined;
  const effectiveStatus = investmentStatus ?? 'active';
  const normalizedStatusDate =
    effectiveStatus === 'exit' || effectiveStatus === 'dead' ? statusDate : undefined;
  if (effectiveStatus !== 'exit') {
    exitTotalEuros = undefined;
  }
  const expectedReturnLeadTime =
    typeof co.expectedReturnLeadTime === 'number' && Number.isFinite(co.expectedReturnLeadTime)
      ? co.expectedReturnLeadTime
      : undefined;
  const expectedReturnMultiple =
    typeof co.expectedReturnMultiple === 'number' && Number.isFinite(co.expectedReturnMultiple)
      ? co.expectedReturnMultiple
      : undefined;
  return {
    ...co,
    ticketSize,
    investmentDate,
    investmentStatus: effectiveStatus,
    countryCode,
    statusDate: normalizedStatusDate,
    exitTotalEuros,
    expectedReturnLeadTime,
    expectedReturnMultiple,
  };
}

type CompanyUpdateClearable = CompanyUpdate & {
  extraction?: FounderUpdateExtraction | null;
};

const UPDATE_SENTIMENTS: UpdateSentiment[] = ['positive', 'neutral', 'negative', 'mixed'];

function normalizeCompanyUpdate(update: CompanyUpdateClearable): CompanyUpdate {
  const { extraction, ...rest } = update;
  const normalized: CompanyUpdate = {
    ...rest,
    aiSummary: typeof rest.aiSummary === 'string' ? rest.aiSummary : '',
    keyPoints: Array.isArray(rest.keyPoints) ? rest.keyPoints : [],
    sentiment: UPDATE_SENTIMENTS.includes(rest.sentiment) ? rest.sentiment : 'neutral',
  };

  if (rest.source === 'email' || rest.source === 'manual') {
    normalized.source = rest.source;
  }
  if (typeof rest.sourceInboxItemId === 'string') {
    normalized.sourceInboxItemId = rest.sourceInboxItemId;
  }
  if (typeof rest.sourceMessageId === 'string') {
    normalized.sourceMessageId = rest.sourceMessageId;
  }
  if (typeof rest.fromEmail === 'string') {
    normalized.fromEmail = rest.fromEmail;
  }
  if (typeof rest.subject === 'string') {
    normalized.subject = rest.subject;
  }
  if (typeof rest.receivedAt === 'string') {
    normalized.receivedAt = rest.receivedAt;
  }
  if (extraction && typeof extraction === 'object') {
    normalized.extraction = extraction;
  }

  return normalized;
}

function companyUpdateToFirestorePayload(
  userId: string,
  update: CompanyUpdateClearable
): Record<string, unknown> {
  const payload = stripUndefinedFields({
    userId,
    ...normalizeCompanyUpdate(update),
  } as Record<string, unknown>);

  if (update.extraction === null) {
    payload.extraction = null;
    payload.sourceInboxItemId = update.sourceInboxItemId ?? null;
    payload.sourceMessageId = update.sourceMessageId ?? null;
    payload.fromEmail = update.fromEmail ?? null;
    payload.subject = update.subject ?? null;
    payload.receivedAt = update.receivedAt ?? null;
  }

  return payload;
}

function docToUpdate(d: QueryDocumentSnapshot): CompanyUpdate {
  const x = d.data() as CompanyUpdateClearable & { userId?: string };
  const { userId: _u, ...rest } = x;
  return normalizeCompanyUpdate(rest);
}

function docToMonthlyUpdate(d: QueryDocumentSnapshot): MonthlyUpdate {
  const x = d.data() as MonthlyUpdate & { userId?: string };
  const { userId: _u, ...rest } = x;
  return {
    ...rest,
    updateIntelligence: normalizeFounderUpdateIntelligence(rest.updateIntelligence),
  };
}

function docToInvestmentCase(d: QueryDocumentSnapshot): InvestmentCase {
  const x = d.data() as InvestmentCase & { userId?: string };
  const { userId: _u, ...rest } = x;
  const now = new Date().toISOString();
  const industry = typeof rest.industry === 'string' ? rest.industry : '';
  const name =
    typeof rest.name === 'string' && rest.name.trim() ? rest.name.trim() : 'Untitled';
  const createdAt = typeof rest.createdAt === 'string' ? rest.createdAt : now;
  const updatedAt = typeof rest.updatedAt === 'string' ? rest.updatedAt : createdAt;
  const founders = Array.isArray(rest.founders)
    ? rest.founders
        .filter((f) => f && typeof f === 'object')
        .map((f, index) =>
          founderForStorage({
            id:
              typeof f.id === 'string' && f.id.trim() ? f.id : `${d.id}-founder-${index}`,
            name: typeof f.name === 'string' ? f.name : undefined,
            linkedinUrl: typeof f.linkedinUrl === 'string' ? f.linkedinUrl : undefined,
            profileText: typeof f.profileText === 'string' ? f.profileText : undefined,
          })
        )
        .filter((f): f is NonNullable<typeof f> => f !== null)
    : undefined;

  return {
    ...rest,
    name,
    industry,
    createdAt,
    updatedAt,
    founderAmbition: clampRating(rest.founderAmbition),
    timeToRevenue: clampRating(rest.timeToRevenue),
    exitExperience: clampRating(rest.exitExperience),
    founderSkillset: clampRating(rest.founderSkillset),
    marketSize: clampRating(rest.marketSize),
    countryCode: normalizeCountryCode(rest.countryCode),
    deckOcrText: typeof rest.deckOcrText === 'string' ? rest.deckOcrText : undefined,
    deckPdfUrl: typeof rest.deckPdfUrl === 'string' ? rest.deckPdfUrl : undefined,
    deckPdfStoragePath:
      typeof rest.deckPdfStoragePath === 'string' ? rest.deckPdfStoragePath : undefined,
    deckPdfFileName: typeof rest.deckPdfFileName === 'string' ? rest.deckPdfFileName : undefined,
    deckIntelligence: normalizeDeckIntelligence(rest.deckIntelligence),
    founders: founders?.length ? founders : undefined,
  };
}

async function writeBootstrapDocuments(
  userId: string,
  data: {
    financialItems: FinancialItem[];
    companies: InvestmentCompany[];
    updates: CompanyUpdate[];
  }
): Promise<void> {
  const rows: Array<{ col: string; id: string; payload: Record<string, unknown> }> = [];
  for (const item of data.financialItems) {
    rows.push({
      col: COLLECTION_FINANCIAL_ITEMS,
      id: ownedItemDocId(userId, item.id),
      payload: stripUndefinedFields({
        userId,
        ...normalizeFinancialItem(item),
      } as Record<string, unknown>),
    });
  }
  for (const item of data.companies) {
    rows.push({
      col: COLLECTION_INVESTMENT_ITEMS,
      id: ownedItemDocId(userId, item.id),
      payload: investmentCompanyToFirestorePayload(userId, item),
    });
  }
  for (const item of data.updates) {
    rows.push({
      col: COLLECTION_COMPANY_UPDATES,
      id: ownedItemDocId(userId, item.id),
      payload: companyUpdateToFirestorePayload(userId, item as CompanyUpdateClearable),
    });
  }

  for (let i = 0; i < rows.length; i += 400) {
    const batch = writeBatch(db);
    for (const r of rows.slice(i, i + 400)) {
      batch.set(doc(db, r.col, r.id), r.payload, { merge: true });
    }
    await batch.commit();
  }
}

type IdSets = { fin: Set<string>; inv: Set<string>; upd: Set<string>; mup: Set<string>; ic: Set<string> };

function idsFor(userId: string, items: { id: string }[]): Set<string> {
  return new Set(items.map((i) => ownedItemDocId(userId, i.id)));
}

async function persistDiff(
  userId: string,
  fin: FinancialItem[],
  co: InvestmentCompany[],
  up: CompanyUpdate[],
  mu: MonthlyUpdate[],
  icases: InvestmentCase[],
  committed: MutableRefObject<IdSets>
): Promise<void> {
  const nextF = idsFor(userId, fin);
  const nextI = idsFor(userId, co);
  const nextU = idsFor(userId, up);
  const nextM = idsFor(userId, mu);
  const nextIc = idsFor(userId, icases);

  const delMissing = async (prev: Set<string>, next: Set<string>, col: string) => {
    const toRemove = Array.from(prev).filter((id) => !next.has(id));
    for (const id of toRemove) {
      await deleteDoc(doc(db, col, id));
    }
  };

  await delMissing(committed.current.fin, nextF, COLLECTION_FINANCIAL_ITEMS);
  await delMissing(committed.current.inv, nextI, COLLECTION_INVESTMENT_ITEMS);
  await delMissing(committed.current.upd, nextU, COLLECTION_COMPANY_UPDATES);
  await delMissing(committed.current.mup, nextM, COLLECTION_MONTHLY_UPDATES);
  await delMissing(committed.current.ic, nextIc, COLLECTION_INVESTMENT_CASES);

  const setChunked = async (col: string, items: { id: string }[], payload: (item: { id: string }) => Record<string, unknown>) => {
    for (let i = 0; i < items.length; i += 400) {
      const batch = writeBatch(db);
      for (const item of items.slice(i, i + 400)) {
        const id = ownedItemDocId(userId, item.id);
        batch.set(doc(db, col, id), payload(item), { merge: true });
      }
      await batch.commit();
    }
  };

  await setChunked(COLLECTION_FINANCIAL_ITEMS, fin, (item) =>
    stripUndefinedFields({
      userId,
      ...normalizeFinancialItem(item as FinancialItem),
    } as Record<string, unknown>)
  );
  await setChunked(COLLECTION_INVESTMENT_ITEMS, co, (item) =>
    investmentCompanyToFirestorePayload(userId, item as InvestmentCompany)
  );
  await setChunked(COLLECTION_COMPANY_UPDATES, up, (item) =>
    companyUpdateToFirestorePayload(userId, item as CompanyUpdateClearable)
  );
  await setChunked(COLLECTION_MONTHLY_UPDATES, mu, (item) =>
    monthlyUpdateToFirestorePayload(userId, item as MonthlyUpdate)
  );
  await setChunked(COLLECTION_INVESTMENT_CASES, icases, (item) =>
    investmentCaseToFirestorePayload(userId, item as InvestmentCase)
  );

  committed.current = { fin: nextF, inv: nextI, upd: nextU, mup: nextM, ic: nextIc };
}

export function useFirestoreWorkspaceState(userId: string): FirestoreWorkspaceState {
  const [financialItems, setFinancialItemsState] = useState<FinancialItem[]>([]);
  const [companies, setCompaniesState] = useState<InvestmentCompany[]>([]);
  const [investmentCases, setInvestmentCasesState] = useState<InvestmentCase[]>([]);
  const [updates, setUpdatesState] = useState<CompanyUpdate[]>([]);
  const [monthlyUpdates, setMonthlyUpdatesState] = useState<MonthlyUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const latestRef = useRef({
    financialItems: [] as FinancialItem[],
    companies: [] as InvestmentCompany[],
    investmentCases: [] as InvestmentCase[],
    updates: [] as CompanyUpdate[],
    monthlyUpdates: [] as MonthlyUpdate[],
  });
  const skipHydratePersist = useRef(true);
  const lastPersistedFingerprint = useRef('');
  const committedIds = useRef<IdSets>({
    fin: new Set(),
    inv: new Set(),
    upd: new Set(),
    mup: new Set(),
    ic: new Set(),
  });

  useEffect(() => {
    latestRef.current = { financialItems, companies, investmentCases, updates, monthlyUpdates };
  }, [financialItems, companies, investmentCases, updates, monthlyUpdates]);

  useEffect(() => {
    let cancelled = false;
    const unsubscribers: (() => void)[] = [];

    const qf = query(collection(db, COLLECTION_FINANCIAL_ITEMS), where('userId', '==', userId));
    const qi = query(collection(db, COLLECTION_INVESTMENT_ITEMS), where('userId', '==', userId));
    const qu = query(collection(db, COLLECTION_COMPANY_UPDATES), where('userId', '==', userId));
    const qm = query(collection(db, COLLECTION_MONTHLY_UPDATES), where('userId', '==', userId));
    const qic = query(collection(db, COLLECTION_INVESTMENT_CASES), where('userId', '==', userId));

    (async () => {
      setLoading(true);
      setError(null);
      skipHydratePersist.current = true;

      try {
        await migrateLegacyWorkspaceStateDocument(userId);
        if (cancelled) return;

        const [cf, ci, cu, cm, cic] = await Promise.all([
          getCountFromServer(qf),
          getCountFromServer(qi),
          getCountFromServer(qu),
          getCountFromServer(qm),
          getCountFromServer(qic),
        ]);
        if (cancelled) return;

        if (
          cf.data().count +
            ci.data().count +
            cu.data().count +
            cm.data().count +
            cic.data().count ===
          0
        ) {
          const payload = resolveBootstrapWorkspace(userId);
          await writeBootstrapDocuments(userId, payload);
          markWorkspaceOwner(userId);
          clearLegacyLocalWorkspaceKeys();
        }

        const [sf, si, su, sm, sic] = await Promise.all([
          getDocs(qf),
          getDocs(qi),
          getDocs(qu),
          getDocs(qm),
          getDocs(qic),
        ]);
        if (cancelled) return;

        const fin = sf.docs.map(docToFinancialItem);
        const inv = si.docs.map(docToCompany);
        const upd = su.docs.map(docToUpdate);
        const mud = sm.docs.map(docToMonthlyUpdate);
        const icases = sic.docs.map(docToInvestmentCase);

        setFinancialItemsState(fin);
        setCompaniesState(inv);
        setUpdatesState(upd);
        setMonthlyUpdatesState(mud);
        setInvestmentCasesState(icases);

        committedIds.current = {
          fin: idsFor(userId, fin),
          inv: idsFor(userId, inv),
          upd: idsFor(userId, upd),
          mup: idsFor(userId, mud),
          ic: idsFor(userId, icases),
        };
        lastPersistedFingerprint.current = fingerprint(fin, inv, upd, mud, icases);

        unsubscribers.push(
          onSnapshot(
            qf,
            (snap) => {
              const next = snap.docs.map(docToFinancialItem);
              setFinancialItemsState(next);
            },
            (e) => setError(e.message)
          ),
          onSnapshot(
            qi,
            (snap) => {
              const next = snap.docs.map(docToCompany);
              setCompaniesState(next);
            },
            (e) => setError(e.message)
          ),
          onSnapshot(
            qu,
            (snap) => {
              const next = snap.docs.map(docToUpdate);
              setUpdatesState(next);
            },
            (e) => setError(e.message)
          ),
          onSnapshot(
            qm,
            (snap) => {
              const next = snap.docs.map(docToMonthlyUpdate);
              setMonthlyUpdatesState(next);
            },
            (e) => setError(e.message)
          ),
          onSnapshot(
            qic,
            (snap) => {
              const next = snap.docs.map(docToInvestmentCase);
              setInvestmentCasesState(next);
            },
            (e) => setError(e.message)
          )
        );
      } catch (e) {
        console.error(e);
        setError(e instanceof Error ? e.message : 'Failed to load data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribers.forEach((u) => u());
    };
  }, [userId]);

  useEffect(() => {
    if (loading) return;
    if (skipHydratePersist.current) {
      skipHydratePersist.current = false;
      return;
    }

    const fp = fingerprint(financialItems, companies, updates, monthlyUpdates, investmentCases);
    if (fp === lastPersistedFingerprint.current) return;

    const t = window.setTimeout(() => {
      const {
        financialItems: fin,
        companies: co,
        updates: up,
        monthlyUpdates: mu,
        investmentCases: icases,
      } = latestRef.current;
      const fp2 = fingerprint(fin, co, up, mu, icases);
      if (fp2 === lastPersistedFingerprint.current) return;

      persistDiff(userId, fin, co, up, mu, icases, committedIds)
        .then(() => {
          lastPersistedFingerprint.current = fp2;
        })
        .catch((e) => {
          console.error('Persist error', e);
          setError(e instanceof Error ? e.message : 'Failed to save');
        });
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(t);
  }, [financialItems, companies, updates, monthlyUpdates, investmentCases, loading, userId]);

  const setFinancialItems = useCallback((action: SetStateAction<FinancialItem[]>) => {
    setFinancialItemsState((prev) => (typeof action === 'function' ? action(prev) : action));
  }, []);

  const setCompanies = useCallback((action: SetStateAction<InvestmentCompany[]>) => {
    setCompaniesState((prev) => (typeof action === 'function' ? action(prev) : action));
  }, []);

  const setUpdates = useCallback((action: SetStateAction<CompanyUpdate[]>) => {
    setUpdatesState((prev) => (typeof action === 'function' ? action(prev) : action));
  }, []);

  const setMonthlyUpdates = useCallback((action: SetStateAction<MonthlyUpdate[]>) => {
    setMonthlyUpdatesState((prev) => (typeof action === 'function' ? action(prev) : action));
  }, []);

  const setInvestmentCases = useCallback((action: SetStateAction<InvestmentCase[]>) => {
    setInvestmentCasesState((prev) => (typeof action === 'function' ? action(prev) : action));
  }, []);

  return useMemo(
    () => ({
      financialItems,
      setFinancialItems,
      companies,
      setCompanies,
      investmentCases,
      setInvestmentCases,
      updates,
      setUpdates,
      monthlyUpdates,
      setMonthlyUpdates,
      loading,
      error,
    }),
    [
      financialItems,
      setFinancialItems,
      companies,
      setCompanies,
      investmentCases,
      setInvestmentCases,
      updates,
      setUpdates,
      monthlyUpdates,
      setMonthlyUpdates,
      loading,
      error,
    ]
  );
}
